/**
 * D-279 — one sign-in per browser, every tab.
 *
 * Every request sends the token in localStorage (`auth_token`), which a sign-in
 * (or sign-out) in ANOTHER tab replaces. This tab still holds the user it was
 * opened with (`useAuthStore`), so it went on showing that user and sending
 * that user's id (a new task's `customer_id` = "me") with the OTHER user's
 * token: the server then refused the task ("הלקוח המבקש אינו שייך ללקוח של
 * הפרויקט") and every list was read as the other user.
 *
 * When ANOTHER USER signs in in another tab (the new token's user id is not
 * this tab's user), this tab is marked "switched": a
 * blocking message asks to refresh (AuthSwitchedOverlay) and every request of
 * this tab is refused until the refresh (api.ts), so nothing is sent as the
 * wrong user. No automatic reload (QX-ב): an unsaved form stays on screen.
 * Not a switch (QA-D279): a sign-out / a 401 in another tab (the token is
 * removed — the next request here gets its own 401 and goes to the login), and
 * the SAME user signing in again (this tab simply goes on with the new token).
 * A change made by THIS tab never fires a `storage` event, so a sign-in here
 * is not affected.
 */
export const AUTH_TOKEN_KEY = 'auth_token';

/** The error code of a request refused because this tab's user was replaced. */
export const AUTH_SWITCHED_CODE = 'AUTH_SWITCHED_IN_OTHER_TAB';

/** The user id inside a JWT (no verification: only to compare who it is), or null. */
export function tokenUserId(token: string): string | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=');
    const payload = JSON.parse(atob(b64)) as { user_id?: unknown };
    return typeof payload.user_id === 'string' ? payload.user_id : null;
  } catch {
    return null;
  }
}

/**
 * Did another tab sign in as ANOTHER user than this tab's (`tabUserId`)?
 * A removed token (sign-out / 401), the same user again, or a tab with no
 * user (the login page) is not a switch. A token whose user cannot be read is
 * treated as another user (never send on a guess).
 */
export function tokenSwitchedUser(
  e: { key: string | null; oldValue: string | null; newValue: string | null },
  tabUserId: string | null | undefined,
): boolean {
  if (e.key !== AUTH_TOKEN_KEY || !e.newValue || e.newValue === e.oldValue || !tabUserId) return false;
  return tokenUserId(e.newValue) !== tabUserId;
}

let switched = false;
const listeners = new Set<() => void>();

/** True from the moment another tab replaced the token until this tab is refreshed. */
export function isAuthSwitchedElsewhere(): boolean {
  return switched;
}

/** For useSyncExternalStore. */
export function subscribeAuthSwitched(cb: () => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

export function markAuthSwitched(): void {
  if (switched) return;
  switched = true;
  listeners.forEach((cb) => cb());
}

/**
 * The rejection of a request refused in a switched tab: the API error shape
 * (`error.message` and `response.data.error.message`) so every screen shows it.
 */
export function authSwitchedError(message: string) {
  const error = { code: AUTH_SWITCHED_CODE, message };
  return { success: false, error, message, response: { status: 0, data: { success: false, error } } };
}

/** Tests only. */
export function resetAuthSwitchedForTests(): void {
  switched = false;
}

interface StorageEventTarget {
  addEventListener: (type: 'storage', cb: (e: StorageEvent) => void) => void;
  removeEventListener: (type: 'storage', cb: (e: StorageEvent) => void) => void;
}

/** Start watching; `tabUserId` reads this tab's user at each event. Returns the stop function. */
export function watchAuthAcrossTabs(
  target: StorageEventTarget,
  tabUserId: () => string | null | undefined,
  onSwitch: () => void = markAuthSwitched,
): () => void {
  const onStorage = (e: StorageEvent) => {
    if (tokenSwitchedUser(e, tabUserId())) onSwitch();
  };
  target.addEventListener('storage', onStorage);
  return () => target.removeEventListener('storage', onStorage);
}
